import React from "react";
import Box from "../../components/Box";
import styled from "styled-components";
import NPSCard from "../../components/NPSCard";
import BarometerCard from "../../components/BarometerCard";
import CritizrCard from "../../components/CritizrCard";
import { useStores } from "../../context/storesContext";
import useSWR from "swr";
import { fetcherWithStoreKey } from "../../utils/fetcher";
import { useSearchParams } from "react-router-dom";
import {
  useOptionNpsBarometer,
  useOptionNpsDrive,
  useOptionNpsServices,
  useOptionNpsStore
} from "../../utils/options";

import { Range } from "../../utils/range";
import { Link } from "react-router-dom";
import { CIconButton, CText } from "@carrefour/design-system-components-react";
import Card from "../../components/Card";
import { useTranslation } from "react-i18next";
import { useKpiVisibility } from "../../kpis/useKpiVisibility";

const CardsContainer = styled(Box)`
  gap: ${(props) => `${props.theme.space?.[2]}px`};
`;

const BoxCustom = styled(Box)<{ gap: string }>`
  gap: ${(props) => props.gap};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 16px; 
`;
const Text = styled(CText)`
  font-family: system-ui;
`;

function Client() {
  const { selectedStore } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();
  const {isModuleExists, isKpiExists} = useKpiVisibility();

  const npsStoreRange = searchParams.get("npsStore") ?? null;
  const npsDriveRange = searchParams.get("npsDrive") ?? null;
  const serviceType = searchParams.get("npsService") ?? null;
  const barometerRange = searchParams.get("npsBaro") ?? null;

  //NPS STORE
  const { data: npsStoreData, error: npsStoreError } = useSWR(
    () =>
      selectedStore?.storeKey && npsStoreRange
        ? [
            `/nps?range=${npsStoreRange}&origin=${"STORE"}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url, storeEan as string)
  );
  //NPS DRIVE
  const { data: npsDriveData, error: npsDriveError } = useSWR(
    () =>
      selectedStore?.storeKey && npsDriveRange
        ? [
            `/nps?range=${npsDriveRange}&origin=${"DRIVE"}`,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url, storeEan as string)
  );
  //NPS Service
  const { data: npsServiceData, error: npsServiceError } = useSWR(
    () => 
      // "services?range=CURRENTMONTH&storeType=MARKET&serviceType=LOCATION"
      selectedStore?.storeKey && serviceType
        ? [
            `/nps/services?range=${
              Range.CurrentMonth
            }&storeType=${selectedStore?.storeType?.toUpperCase()}&serviceType=${serviceType}`,
            selectedStore?.storeKey as string, selectedStore.countryKey as string
          ]
        : null,
    ([url, storeEan, countryKey]) =>
      fetcherWithStoreKey(url, storeEan as string, countryKey as string)
  );
  const { data: npsBarometerWeekData, error: npsBarometerWeekError } = useSWR(
    () =>
      selectedStore?.storeKey
        ? [
            `/nps/barometer?range=${Range.CurrentWeek}&storeType=${selectedStore?.storeType?.toUpperCase()}`,
            selectedStore?.storeKey as string, selectedStore?.countryKey as string
          ]
        : null,
    ([url, storeEan, countryKey]) =>
      fetcherWithStoreKey(url, storeEan as string, countryKey as string)
  );
  const { data: npsBarometerMonthData, error: npsBarometerMonthError } = useSWR(
    () =>
      selectedStore?.storeKey
        ? [
            `/nps/barometer?range=${Range.CurrentMonth}&storeType=${selectedStore?.storeType?.toUpperCase()}`,
            selectedStore?.storeKey as string, selectedStore?.countryKey as string
          ]
        : null,
    ([url, storeEan, countryKey]) =>
      fetcherWithStoreKey(url, storeEan as string, countryKey as string)
  );
  //NPS goodDays
  const { data: npsGoodaysData, error: npsGoodaysError } = useSWR(
    () =>
      // "nps/goodays"
      selectedStore?.storeKey && isKpiExists("Client", "NpsGoodays")
        ? [
            ``,
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url, storeEan as string)
  );

  const { t } = useTranslation();

  const optionsNpsStore = useOptionNpsStore();
  const optionNpsDrive = useOptionNpsDrive();
  const optionNpsBarometer = useOptionNpsBarometer();
  const optionNpsServices = useOptionNpsServices();
  const hasBarometerData = (data: any, error: any) => {
    return !error && Array.isArray(data) && data.some((item) => item?.questionCode != null);
  };

  const dynamicOptionNpsBarometer = React.useMemo(() => {
    return optionNpsBarometer.filter((option) => {
      if (option.value === Range.CurrentWeek) {
        return hasBarometerData(npsBarometerWeekData, npsBarometerWeekError);
      }
      if (option.value === Range.CurrentMonth) {
        return hasBarometerData(npsBarometerMonthData, npsBarometerMonthError);
      }
      return false;
    });
  }, [optionNpsBarometer, npsBarometerWeekData, npsBarometerWeekError, npsBarometerMonthData, npsBarometerMonthError]);

  React.useEffect(() => {
    if (dynamicOptionNpsBarometer.length === 0) {
      return;
    }

    const availableRanges = dynamicOptionNpsBarometer.map((option) => option.value);
    if (!barometerRange || !availableRanges.includes(barometerRange)) {
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set("npsBaro", dynamicOptionNpsBarometer[0].value);
      setSearchParams(nextSearchParams);
    }
  }, [barometerRange, dynamicOptionNpsBarometer, searchParams, setSearchParams]);

  const selectedBarometerRange = dynamicOptionNpsBarometer.length > 0
    ? (dynamicOptionNpsBarometer.some((option) => option.value === barometerRange)
      ? barometerRange
      : dynamicOptionNpsBarometer[0].value)
    : null;
  const npsBarometerData = selectedBarometerRange === Range.CurrentMonth
    ? npsBarometerMonthData
    : selectedBarometerRange === Range.CurrentWeek
      ? npsBarometerWeekData
      : null;
  const npsBarometerError = selectedBarometerRange === Range.CurrentMonth
    ? npsBarometerMonthError
    : selectedBarometerRange === Range.CurrentWeek
      ? npsBarometerWeekError
      : null;
  const isBarometerWeekLoading = npsBarometerWeekData === undefined && !npsBarometerWeekError;
  const isBarometerMonthLoading = npsBarometerMonthData === undefined && !npsBarometerMonthError;
  const isBarometerLoading = isBarometerWeekLoading || isBarometerMonthLoading;
  const shouldDisplayBarometer = isBarometerLoading || dynamicOptionNpsBarometer.length > 0;
  
  const CieCard = ({ ...res }: any) => {
    return (
      <Card>
        <BoxCustom gap={"14px"}>
            <Text bold tagStyle={"p"}> {t('client.msg_analyse_cie')}
            </Text>
            <Link
              to={window?.config?.EXTERNAL_URLS?.CIE}
              target="_blank"
              data-testid={"external-link"}
            >
              <CIconButton
                icon={"external-link"}
                variation={"tertiary"}
                tone={"main"}
                size={"m"}
              />
            </Link>
        </BoxCustom>
      </Card>
    );
  };

  if (!isModuleExists("Client")) {
    return null;
  }

  return (
    <CardsContainer
      width={"100%"}
      display={"flex"}
      flexDirection={"column"}
      justifyContent={"center"}
    >
      { isKpiExists("Client", "NpsStore") && (
        <NPSCard
          isLoading={!npsStoreData}
          isUnavailable={!!npsStoreError || npsStoreData === null || npsStoreData?.neutrals === null}
          npsData={npsStoreData}
          options={optionsNpsStore}
          param={"npsStore"}
          title={t('client.nps_magasin')}
        />
      )}
      { isKpiExists("Client", "NpsDrive") && (
        <NPSCard
          isLoading={!npsDriveData}
          isUnavailable={!!npsDriveError || npsDriveData === null || npsDriveData?.neutrals === null}
          npsData={npsDriveData}
          options={optionNpsDrive}
          param={"npsDrive"}
          title={t('client.nps_drive')}
        />
      )}
       { isKpiExists("Client", "NpsService") && (
        <NPSCard
          isLoading={!npsServiceData}
          isUnavailable={!!npsServiceError || npsServiceData === null || npsServiceData?.neutrals === null}
          npsData={npsServiceData}
          options={optionNpsServices}
          param={"npsService"}
          title={t('client.nps_services_mois_encours')}
        />
       )}
       { isKpiExists("Client", "NpsBarometer") && shouldDisplayBarometer && (
        <BarometerCard
          title={t('client.barometre_chaud')}
          isLoading={isBarometerLoading || !npsBarometerData}
          isUnavailable={!!npsBarometerError || npsBarometerData === null || npsBarometerData?.[0]?.questionCode === null}
          scoreData={npsBarometerData}
          options={dynamicOptionNpsBarometer}
          param={"npsBaro"}
          openInformationModal={() => null}
        />
       )}
      {false && isKpiExists("Client", "NpsGoodays") && (
       <CritizrCard
          CritizrData={npsGoodaysData}
          isLoading={!npsGoodaysData}
          isUnavailable={!!npsGoodaysError || npsGoodaysData === null}
        />
      )}
      {isKpiExists("Client", "CIEAnalysis") && (
        <CieCard/>
      )}
    </CardsContainer>
  );
}

export default Client;
